/* Kocktail — cart UI that must keep working when nothing else does: the gift
   note, and repainting the volume discount bars.

   --- gift note ---

   The controls in snippets/kocktail-cart-gifts.liquid are real forms posting
   to /cart/add and /cart/change. Everything here is an upgrade on top of them,
   never the thing that makes them work: it intercepts the submit and does the
   same request without leaving the drawer. If any of it throws, the form is
   submitted for real, so the worst case is a page reload rather than a button
   that does nothing.

   That split is the lesson from the version before it, which did the whole job
   in fetch. When the script did not run the customer got a switch that flipped
   (the label does that natively), a box that took text, and a button that did
   nothing — indistinguishable from a feature that works.

   --- volume bars ---

   Drawn correctly by Liquid, which counts the cart on the server. This only
   keeps them in step with changes made afterwards, which matters on the
   product page, where adding to the cart re-renders nothing.

   They live in the same file because every separate script is another thing
   that has to actually reach the storefront, and both want the same trigger:
   the cart changed. */
(function () {
  var ROUTES = (typeof Theme !== 'undefined' && Theme.routes) || {};
  var ADD_URL = ROUTES.cart_add_url || '/cart/add.js';
  var CHANGE_URL = ROUTES.cart_change_url || '/cart/change.js';
  var UPDATE_URL = ROUTES.cart_update_url || '/cart/update.js';
  var CART_URL = ROUTES.cart_url || '/cart';

  function readCart() {
    return fetch(CART_URL + '.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  // Re-render the cart section ourselves. Only used when the events module is
  // not around to be asked; when it is, its announce path is the one the rest
  // of the theme already listens to.
  function renderCartSections() {
    var comps = Array.prototype.slice.call(document.querySelectorAll('cart-items-component[data-section-id]'));
    var ids = [];
    comps.forEach(function (c) {
      var id = c.getAttribute('data-section-id');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    if (!ids.length) return Promise.resolve();
    return fetch(CART_URL + '?sections=' + encodeURIComponent(ids.join(',')), {
      headers: { Accept: 'application/json' },
    })
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        comps.forEach(function (c) {
          var id = c.getAttribute('data-section-id');
          var html = sections[id];
          if (!html) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var next = doc.querySelector('cart-items-component[data-section-id="' + id + '"]');
          if (next && c.parentNode) c.replaceWith(next);
        });
      })
      .catch(function () {});
  }

  function announce(cart, action, id) {
    var hook = window.KocktailCart;
    if (hook && typeof hook.announce === 'function') return hook.announce(cart, action, id);
    return renderCartSections();
  }

  function state(item, text, isError) {
    var el = item && item.querySelector('[data-kx-gift-state]');
    if (!el) return;
    el.textContent = text;
    if (isError) el.setAttribute('data-kx-error', '');
    else el.removeAttribute('data-kx-error');
  }

  // Do what the form would have done, without the reload.
  //
  // quantity is what the pressed button carries: 1 saves, 0 removes, exactly
  // as /cart/change reads it natively.
  function submitGiftForm(form, quantity) {
    var item = form.closest('.kx-cgift-item');
    var action = form.getAttribute('data-kx-gift-action');
    var ta = form.querySelector('[data-kx-gift-msg]');
    var btn = form.querySelector('[data-kx-gift-save]');
    var value = ta ? ta.value || '' : '';
    var prop = (item && item.getAttribute('data-kx-gift-prop')) || 'Message';

    if (action === 'add' && !value) {
      state(item, "Write your message and we'll add the card");
      if (ta) ta.focus();
      return Promise.resolve();
    }

    var url;
    var body;
    if (action === 'add') {
      var picked = form.querySelector('[data-kx-gift-card]:checked') || form.querySelector('input[name="id"]');
      var vid = picked && parseInt(picked.value, 10);
      if (!vid) return Promise.reject(new Error('no gift variant'));
      url = ADD_URL;
      body = { id: vid, quantity: 1, properties: {} };
      body.properties[prop] = value;
      state(item, 'Adding…');
    } else {
      var key = form.querySelector('input[name="id"]');
      if (!key || !key.value) return Promise.reject(new Error('no gift line'));
      url = CHANGE_URL;
      body = { id: key.value, quantity: quantity };
      // A line on its way out has no message to carry, and sending one would
      // rewrite the properties of the thing being removed.
      if (quantity > 0) {
        body.properties = {};
        body.properties[prop] = value;
      }
      state(item, quantity > 0 ? 'Saving…' : 'Removing…');
    }

    if (btn) btn.setAttribute('aria-busy', 'true');
    if (item) item.setAttribute('aria-busy', 'true');

    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    })
      .then(function (r) {
        if (!r.ok) throw new Error('gift request failed');
        return r.json();
      })
      .then(readCart)
      .then(function (cart) {
        if (ta) ta.setAttribute('data-kx-saved', value);
        // The drawer re-renders from the server after this, which is what
        // swaps the add form for the edit form and puts the new line key on
        // it. Nothing here needs to patch that by hand.
        var done = announce(cart, quantity === 0 ? 'remove' : 'add', body.id);
        scheduleVolume();
        return done;
      })
      .finally(function () {
        if (btn) btn.removeAttribute('aria-busy');
        if (item) item.removeAttribute('aria-busy');
      });
  }

  // Last-ditch save for a message still unsent when the page goes away.
  // Clicking Checkout blurs the textarea and navigates in the same breath, so
  // a normal fetch is cancelled in flight and the note reaches the order
  // missing. sendBeacon is the one request a browser promises to finish after
  // the page is gone.
  //
  // Only for a note already in the basket. Creating one is the button's job,
  // and doing it silently on the way out would add a charge nobody agreed to.
  function flush() {
    if (!navigator.sendBeacon) return;
    document.querySelectorAll('[data-kx-gift-form][data-kx-gift-action="update"]').forEach(function (form) {
      var ta = form.querySelector('[data-kx-gift-msg]');
      var key = form.querySelector('input[name="id"]');
      if (!ta || !key || !key.value) return;
      var value = ta.value || '';
      if (value === (ta.getAttribute('data-kx-saved') || '')) return;
      var item = form.closest('.kx-cgift-item');
      var props = {};
      props[(item && item.getAttribute('data-kx-gift-prop')) || 'Message'] = value;
      navigator.sendBeacon(
        CHANGE_URL,
        new Blob([JSON.stringify({ id: key.value, quantity: 1, properties: props })], {
          type: 'application/json',
        })
      );
      ta.setAttribute('data-kx-saved', value);
    });
  }

  /* ---- volume discount bars ---- */

  function volQty(el, cart) {
    var raw = el.getAttribute('data-kx-vol-ids') || '';
    var ids = {};
    var hasIds = false;
    raw.split(',').forEach(function (s) {
      s = s.trim();
      if (s) {
        ids[s] = true;
        hasIds = true;
      }
    });
    var type = (el.getAttribute('data-kx-vol-type') || '').trim().toLowerCase();
    return (cart.items || []).reduce(function (n, item) {
      var ok;
      if (hasIds) ok = !!ids[String(item.product_id)];
      else if (type) ok = String(item.product_type || '').toLowerCase() === type;
      else ok = true;
      return ok ? n + item.quantity : n;
    }, 0);
  }

  function volPaint(el, qty) {
    var t1 = parseInt(el.getAttribute('data-kx-vol-t1'), 10) || 0;
    var t2 = parseInt(el.getAttribute('data-kx-vol-t2'), 10) || 0;
    var l1 = el.getAttribute('data-kx-vol-l1') || '';
    var l2 = el.getAttribute('data-kx-vol-l2') || '';
    // The top of the scale is the furthest tier, so a full bar means there is
    // nothing left to earn.
    var top = Math.max(t1, t2);
    if (!top) return;

    if (el.hasAttribute('data-kx-vol-hide-empty')) el.hidden = qty === 0;

    var fill = el.querySelector('[data-kx-vol-fill]');
    if (fill) fill.style.width = Math.min(100, Math.round((qty / top) * 100)) + '%';

    var track = el.querySelector('[data-kx-vol-track]');
    if (track) {
      track.setAttribute('aria-valuenow', String(qty));
      track.setAttribute('aria-valuetext', qty + ' of ' + top);
    }

    el.querySelectorAll('[data-kx-vol-step]').forEach(function (st) {
      var at = parseInt(st.getAttribute('data-kx-vol-step'), 10) || 0;
      st.classList.toggle('is-on', qty >= at);
    });
    el.classList.toggle('is-empty', qty === 0);

    var msg = el.querySelector('[data-kx-vol-msg]');
    if (!msg) return;
    if (t2 && qty >= t2) msg.textContent = l2 + ' applied at checkout';
    else if (t1 && qty >= t1) msg.textContent = l1 + ' unlocked · add ' + (t2 - qty) + ' more for ' + l2;
    else if (t1) msg.textContent = 'Add ' + (t1 - qty) + ' more for ' + l1;
    else msg.textContent = 'Add ' + (t2 - qty) + ' more for ' + l2;
  }

  function repaintVolumeBars() {
    var bars = document.querySelectorAll('[data-kx-vol]');
    if (!bars.length) return;
    readCart()
      .then(function (cart) {
        bars.forEach(function (el) { volPaint(el, volQty(el, cart)); });
      })
      .catch(function () { /* leave the server-rendered figure alone */ });
  }

  // Producers dispatch lines-update BEFORE their network request and hand
  // consumers that request's promise on event.promise (see cart-icon.js). A
  // fixed delay here used to read /cart.js while the POST was still in
  // flight, painting the pre-add count. So: wait for the mutation to land,
  // THEN debounce — a three-glass bundle adds a line at a time, and the
  // debounce collapses the burst into one cart read after the last one.
  var volTimer = null;
  function queueVolumeRepaint() {
    clearTimeout(volTimer);
    volTimer = setTimeout(repaintVolumeBars, 60);
  }
  function scheduleVolume(e) {
    var p = e && e.promise;
    if (p && typeof p.then === 'function') {
      p.then(queueVolumeRepaint, queueVolumeRepaint);
    } else {
      queueVolumeRepaint();
    }
  }

  var typeTimer = null;

  function init() {
    document.addEventListener('shopify:cart:lines-update', scheduleVolume);
    document.addEventListener('shopify:cart:discount-update', scheduleVolume);
    // A page restored from the back/forward cache can be showing a cart that
    // has moved on since it was drawn.
    window.addEventListener('pageshow', function (e) { if (e.persisted) scheduleVolume(); });
    window.addEventListener('pagehide', flush);

    // All delegated, because the drawer replaces this markup on every cart
    // change and anything bound to an element goes with it.
    //
    // submit, not a click on the button: it covers the Enter key and the
    // browser's own validation, and it is the event a form actually has.
    document.addEventListener('submit', function (e) {
      var form = e.target && e.target.matches && e.target.matches('[data-kx-gift-form]') ? e.target : null;
      if (!form) return;
      var pressed = e.submitter;
      var quantity = pressed && pressed.name === 'quantity' ? parseInt(pressed.value, 10) : 1;
      e.preventDefault();
      submitGiftForm(form, quantity).catch(function () {
        // Fall back to the real thing. A reload is worse than staying in the
        // drawer, and far better than nothing happening.
        form.submit();
      });
    });

    document.addEventListener('input', function (e) {
      var ta = e.target && e.target.matches && e.target.matches('[data-kx-gift-msg]') ? e.target : null;
      if (!ta) return;
      var item = ta.closest('.kx-cgift-item');
      if ((ta.value || '') === (ta.getAttribute('data-kx-saved') || '')) return;
      state(item, '');
      // Autosave a moment after typing stops, but only for a note already in
      // the basket. Adding one costs money, so that stays on the button.
      var form = ta.closest('[data-kx-gift-form]');
      if (!form || form.getAttribute('data-kx-gift-action') !== 'update') return;
      clearTimeout(typeTimer);
      typeTimer = setTimeout(function () {
        submitGiftForm(form, 1).catch(function () {});
      }, 900);
    });

    // Fallback for when no gift product is linked: plain cart attributes.
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || !t.matches || !t.matches('[data-kx-cart-attr]')) return;
      var name = t.getAttribute('data-kx-cart-attr');
      var value = t.type === 'checkbox' ? (t.checked ? t.getAttribute('data-on') || 'Yes' : '') : t.value || '';
      var attrs = {};
      attrs[name] = value;
      t.setAttribute('aria-busy', 'true');
      fetch(UPDATE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: attrs }),
      })
        .catch(function () { if (t.type === 'checkbox') t.checked = !t.checked; })
        .finally(function () { t.removeAttribute('aria-busy'); });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
